import React, { useState, useEffect, useCallback } from 'react';
import { Lock, LogOut, Plus, Trash2, Edit3, X, Package, Mail, ShoppingBag, Star, MessageSquare, Save } from 'lucide-react';
import { adminLogin, adminMe, fetchProducts, createProduct, updateProduct, deleteProduct, adminNewsletter, adminOrders, adminContact, adminReviews, deleteReview } from '../lib/api';

const CATS = ['zelki', 'czekoladki', 'syropy', 'herbaty', 'shoty', 'tabletki', 'conez'];
const emptyProduct = { name: '', subtitle: '', category: 'zelki', price: 0, oldPrice: '', thc: '', image: '', images: [], video: '', inStock: true, badge: '', rating: 5, reviews: 0, description: '' };

const Admin = () => {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [creds, setCreds] = useState({ username: '', password: '' });
  const [err, setErr] = useState('');
  const [tab, setTab] = useState('products');

  const [products, setProducts] = useState([]);
  const [newsletter, setNewsletter] = useState([]);
  const [orders, setOrders] = useState([]);
  const [messages, setMessages] = useState([]);
  const [reviews, setReviews] = useState([]);

  const [editing, setEditing] = useState(null); // product object or null
  const [saving, setSaving] = useState(false);

  const checkAuth = useCallback(async () => {
    try {
      await adminMe();
      setAuthed(true);
    } catch {
      setAuthed(false);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const loadAll = useCallback(async () => {
    try {
      const [p, n, o, c, r] = await Promise.all([
        fetchProducts(), adminNewsletter(), adminOrders(), adminContact(), adminReviews(),
      ]);
      setProducts(p); setNewsletter(n); setOrders(o); setMessages(c); setReviews(r);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { if (authed) loadAll(); }, [authed, loadAll]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setErr('');
    try {
      const { token } = await adminLogin(creds.username, creds.password);
      localStorage.setItem('cb_admin_token', token);
      setAuthed(true);
    } catch (e2) {
      setErr('Nieprawidlowy login lub haslo');
    }
  };

  const logout = () => { localStorage.removeItem('cb_admin_token'); setAuthed(false); };

  const saveProduct = async (e) => {
    e.preventDefault();
    setSaving(true);
    const imgs = (editing.images && editing.images.length ? editing.images : (editing.image ? [editing.image] : [])).filter(Boolean);
    const payload = {
      ...editing,
      price: parseFloat(editing.price) || 0,
      oldPrice: editing.oldPrice === '' || editing.oldPrice === null ? null : parseFloat(editing.oldPrice),
      rating: parseFloat(editing.rating) || 5,
      reviews: parseInt(editing.reviews) || 0,
      badge: editing.badge || null,
      image: editing.image || imgs[0] || '',
      images: imgs,
      video: editing.video || null,
    };
    try {
      if (editing.id) await updateProduct(editing.id, payload);
      else await createProduct(payload);
      setEditing(null);
      await loadAll();
    } catch (e2) { alert('Blad zapisu'); }
    finally { setSaving(false); }
  };

  const removeProduct = async (id) => {
    if (!window.confirm('Usunac produkt?')) return;
    await deleteProduct(id);
    await loadAll();
  };

  const removeReview = async (id) => {
    if (!window.confirm('Usunac opinie?')) return;
    await deleteReview(id);
    await loadAll();
  };

  if (checking) {
    return <div className="min-h-screen grid place-items-center text-white/60">Ladowanie...</div>;
  }

  if (!authed) {
    return (
      <div className="min-h-screen grid place-items-center px-4 smoke-red">
        <form onSubmit={handleLogin} className="w-full max-w-sm cb-panel border border-white/10 rounded-2xl p-8">
          <div className="mx-auto mb-5 h-14 w-14 rounded-full border-2 border-[#a6e22e] grid place-items-center text-[#a6e22e]"><Lock size={24} /></div>
          <h1 className="font-anton text-2xl uppercase text-center mb-6">Panel <span className="graffiti-gradient">Admina</span></h1>
          <input value={creds.username} onChange={(e) => setCreds({ ...creds, username: e.target.value })} placeholder="Login" className="w-full mb-3 rounded-xl bg-black/60 border border-white/15 px-4 py-3 text-sm outline-none focus:border-[#a6e22e]" />
          <input type="password" value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })} placeholder="Haslo" className="w-full mb-4 rounded-xl bg-black/60 border border-white/15 px-4 py-3 text-sm outline-none focus:border-[#a6e22e]" />
          {err && <p className="text-[#ff5a5a] text-sm mb-3">{err}</p>}
          <button className="btn-green w-full py-3">Zaloguj</button>
        </form>
      </div>
    );
  }

  const tabs = [
    { id: 'products', label: 'Produkty', icon: Package, count: products.length },
    { id: 'orders', label: 'Zamowienia', icon: ShoppingBag, count: orders.length },
    { id: 'newsletter', label: 'Newsletter', icon: Mail, count: newsletter.length },
    { id: 'messages', label: 'Wiadomosci', icon: MessageSquare, count: messages.length },
    { id: 'reviews', label: 'Opinie', icon: Star, count: reviews.length },
  ];

  return (
    <div className="min-h-screen">
      <header className="border-b border-white/10 px-4 lg:px-8 h-16 flex items-center justify-between">
        <h1 className="font-anton uppercase text-xl">Cali Boyz <span className="graffiti-gradient">Admin</span></h1>
        <button onClick={logout} className="btn-ghost-red px-4 py-2 text-sm inline-flex items-center gap-2"><LogOut size={15} /> Wyloguj</button>
      </header>

      <div className="px-4 lg:px-8 py-6">
        <div className="flex flex-wrap gap-2 mb-6">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2 transition ${tab === t.id ? 'btn-green' : 'bg-white/5 text-white/70 border border-white/10 hover:text-[#a6e22e]'}`}>
              <t.icon size={15} /> {t.label} <span className="opacity-70">({t.count})</span>
            </button>
          ))}
        </div>

        {tab === 'products' && (
          <div>
            <button onClick={() => setEditing({ ...emptyProduct })} className="btn-green px-5 py-2.5 inline-flex items-center gap-2 mb-4"><Plus size={16} /> Dodaj produkt</button>
            <div className="grid gap-3">
              {products.map((p) => (
                <div key={p.id} className="cb-panel border border-white/10 rounded-xl p-3 flex items-center gap-4">
                  <img src={p.image} alt="" className="h-14 w-14 object-contain rounded-lg bg-black/40" />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold truncate">{p.name}</p>
                    <p className="text-white/50 text-xs">{p.category} • {p.thc} • {p.inStock ? 'dostepny' : 'niedostepny'}</p>
                  </div>
                  <span className="text-[#a6e22e] font-bold">{p.price.toFixed(2)} zl</span>
                  <button onClick={() => setEditing({ ...p, oldPrice: p.oldPrice ?? '', badge: p.badge ?? '', images: p.images && p.images.length ? p.images : (p.image ? [p.image] : []), video: p.video ?? '' })} className="h-9 w-9 grid place-items-center rounded-full bg-white/10 hover:bg-white/20"><Edit3 size={15} /></button>
                  <button onClick={() => removeProduct(p.id)} className="h-9 w-9 grid place-items-center rounded-full bg-white/10 hover:bg-[#ff2e2e]/30 text-[#ff5a5a]"><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === 'orders' && (
          <div className="grid gap-3">
            {orders.length === 0 && <p className="text-white/50">Brak zamowien.</p>}
            {orders.map((o) => (
              <div key={o.id} className="cb-panel border border-white/10 rounded-xl p-4">
                <div className="flex justify-between mb-2"><span className="text-white/50 text-xs">{new Date(o.createdAt).toLocaleString('pl-PL')}</span><span className="text-[#a6e22e] font-bold">{o.total.toFixed(2)} zl</span></div>
                {o.name && <p className="text-sm">Klient: {o.name}</p>}
                <ul className="text-sm text-white/70 mt-1">
                  {o.items.map((i, k) => <li key={k}>{i.qty}x {i.name} ({i.thc})</li>)}
                </ul>
                {o.note && <p className="text-white/50 text-xs mt-1">Uwagi: {o.note}</p>}
              </div>
            ))}
          </div>
        )}

        {tab === 'newsletter' && (
          <div className="cb-panel border border-white/10 rounded-xl divide-y divide-white/5">
            {newsletter.length === 0 && <p className="text-white/50 p-4">Brak zapisow.</p>}
            {newsletter.map((n) => (
              <div key={n.id} className="px-4 py-3 flex justify-between text-sm"><span>{n.email}</span><span className="text-white/40">{new Date(n.createdAt).toLocaleDateString('pl-PL')}</span></div>
            ))}
          </div>
        )}

        {tab === 'messages' && (
          <div className="grid gap-3">
            {messages.length === 0 && <p className="text-white/50">Brak wiadomosci.</p>}
            {messages.map((m) => (
              <div key={m.id} className="cb-panel border border-white/10 rounded-xl p-4">
                <div className="flex justify-between"><span className="font-semibold">{m.name}</span><span className="text-white/40 text-xs">{new Date(m.createdAt).toLocaleString('pl-PL')}</span></div>
                <p className="text-[#a6e22e] text-xs">{m.email}</p>
                <p className="text-white/70 text-sm mt-2">{m.message}</p>
              </div>
            ))}
          </div>
        )}

        {tab === 'reviews' && (
          <div className="grid gap-3">
            {reviews.map((r) => (
              <div key={r.id} className="cb-panel border border-white/10 rounded-xl p-4 flex items-start gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-1 text-[#ffb000] mb-1">{Array.from({ length: 5 }).map((_, i) => <Star key={i} size={12} className={i < r.rating ? 'fill-[#ffb000]' : 'opacity-30'} />)}</div>
                  <p className="text-sm">{r.text}</p>
                  <p className="text-white/40 text-xs mt-1">{r.name} {r.product ? `• ${r.product}` : ''}</p>
                </div>
                <button onClick={() => removeReview(r.id)} className="h-9 w-9 grid place-items-center rounded-full bg-white/10 hover:bg-[#ff2e2e]/30 text-[#ff5a5a]"><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 z-[100] bg-black/80 grid place-items-center p-4 overflow-y-auto">
          <form onSubmit={saveProduct} className="w-full max-w-lg cb-panel border border-white/10 rounded-2xl p-6 my-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-anton uppercase text-lg">{editing.id ? 'Edytuj' : 'Nowy'} produkt</h2>
              <button type="button" onClick={() => setEditing(null)} className="h-9 w-9 grid place-items-center rounded-full hover:bg-white/10"><X size={18} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="col-span-2 text-xs text-white/60">Nazwa<input required value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="mt-1 w-full rounded-lg bg-black/60 border border-white/15 px-3 py-2 text-sm outline-none focus:border-[#a6e22e]" /></label>
              <label className="col-span-2 text-xs text-white/60">Podtytul<input value={editing.subtitle} onChange={(e) => setEditing({ ...editing, subtitle: e.target.value })} className="mt-1 w-full rounded-lg bg-black/60 border border-white/15 px-3 py-2 text-sm outline-none focus:border-[#a6e22e]" /></label>
              <label className="text-xs text-white/60">Kategoria<select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="mt-1 w-full rounded-lg bg-black/60 border border-white/15 px-3 py-2 text-sm outline-none focus:border-[#a6e22e]">{CATS.map((c) => <option key={c} value={c}>{c}</option>)}</select></label>
              <label className="text-xs text-white/60">THC<input value={editing.thc} onChange={(e) => setEditing({ ...editing, thc: e.target.value })} className="mt-1 w-full rounded-lg bg-black/60 border border-white/15 px-3 py-2 text-sm outline-none focus:border-[#a6e22e]" /></label>
              <label className="text-xs text-white/60">Cena (zl)<input type="number" step="0.01" required value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} className="mt-1 w-full rounded-lg bg-black/60 border border-white/15 px-3 py-2 text-sm outline-none focus:border-[#a6e22e]" /></label>
              <label className="text-xs text-white/60">Stara cena<input type="number" step="0.01" value={editing.oldPrice} onChange={(e) => setEditing({ ...editing, oldPrice: e.target.value })} className="mt-1 w-full rounded-lg bg-black/60 border border-white/15 px-3 py-2 text-sm outline-none focus:border-[#a6e22e]" /></label>
              <label className="col-span-2 text-xs text-white/60">Glowne zdjecie (URL)<input value={editing.image} onChange={(e) => setEditing({ ...editing, image: e.target.value })} placeholder="/products/zelki.webp" className="mt-1 w-full rounded-lg bg-black/60 border border-white/15 px-3 py-2 text-sm outline-none focus:border-[#a6e22e]" /></label>
              <div className="col-span-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-white/60">Galeria zdjec (mozesz dodac wiele)</span>
                  <button type="button" onClick={() => setEditing({ ...editing, images: [...(editing.images || []), ''] })} className="text-xs text-[#a6e22e] inline-flex items-center gap-1"><Plus size={12} /> Dodaj zdjecie</button>
                </div>
                <div className="space-y-2">
                  {(editing.images || []).map((img, k) => (
                    <div key={k} className="flex items-center gap-2">
                      {img ? <img src={img} alt="" className="h-9 w-9 object-contain rounded bg-black/40 shrink-0" /> : <span className="h-9 w-9 rounded bg-black/40 shrink-0" />}
                      <input value={img} onChange={(e) => { const arr = [...editing.images]; arr[k] = e.target.value; setEditing({ ...editing, images: arr }); }} placeholder="/products/... lub https://..." className="flex-1 rounded-lg bg-black/60 border border-white/15 px-3 py-2 text-sm outline-none focus:border-[#a6e22e]" />
                      <button type="button" onClick={() => setEditing({ ...editing, images: editing.images.filter((_, ii) => ii !== k) })} className="h-8 w-8 grid place-items-center rounded-full bg-white/10 hover:bg-[#ff2e2e]/30 text-[#ff5a5a] shrink-0"><Trash2 size={14} /></button>
                    </div>
                  ))}
                  {(!editing.images || editing.images.length === 0) && <p className="text-white/40 text-xs">Brak dodatkowych zdjec. Uzyte zostanie glowne zdjecie.</p>}
                </div>
              </div>
              <label className="col-span-2 text-xs text-white/60">Film (URL YouTube lub .mp4)<input value={editing.video} onChange={(e) => setEditing({ ...editing, video: e.target.value })} placeholder="https://youtube.com/watch?v=..." className="mt-1 w-full rounded-lg bg-black/60 border border-white/15 px-3 py-2 text-sm outline-none focus:border-[#a6e22e]" /></label>
              <label className="text-xs text-white/60">Badge<input value={editing.badge} onChange={(e) => setEditing({ ...editing, badge: e.target.value })} placeholder="PROMO / BESTSELLER" className="mt-1 w-full rounded-lg bg-black/60 border border-white/15 px-3 py-2 text-sm outline-none focus:border-[#a6e22e]" /></label>
              <label className="text-xs text-white/60">Ocena<input type="number" step="0.1" min="0" max="5" value={editing.rating} onChange={(e) => setEditing({ ...editing, rating: e.target.value })} className="mt-1 w-full rounded-lg bg-black/60 border border-white/15 px-3 py-2 text-sm outline-none focus:border-[#a6e22e]" /></label>
              <label className="col-span-2 text-xs text-white/60">Opis<textarea rows={3} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="mt-1 w-full rounded-lg bg-black/60 border border-white/15 px-3 py-2 text-sm outline-none focus:border-[#a6e22e] resize-none" /></label>
              <label className="col-span-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.inStock} onChange={(e) => setEditing({ ...editing, inStock: e.target.checked })} /> Dostepny</label>
            </div>
            <button disabled={saving} className="btn-green w-full py-3 mt-4 inline-flex items-center justify-center gap-2"><Save size={16} /> {saving ? 'Zapisywanie...' : 'Zapisz'}</button>
          </form>
        </div>
      )}
    </div>
  );
};

export default Admin;
